import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
// RW-1205: reads/writes go through the data.js adapter (Rule 12) — no
// direct Firestore SDK imports. serverTimestamp is the adapter's re-export.
import { withTenant, setDocument, serverTimestamp } from '../services/data';
import { colors, fonts, radius, spacing } from './theme';
import { useCelebration } from './CelebrationEngine';
import FileUpload from './FileUpload';
import { trackWithUser, EVENTS } from '../analytics/track';
// RW-773: shared profile-field definitions (single source of truth with
// ProfileEditor). Default-import the picker as TerritorySection so the JSX
// below is unchanged.
import { OFFICE_OPTIONS as OFFICES, SPECIALTIES, SERVICE_AREA_GROUPS, toggleItem, hasProfilePhone } from '../lib/profileFields';
import TerritorySection from './ServiceAreaPicker';
// RW-2266: a refresh used to throw the answers away — the resume point lives
// in this browser until the profile write lands.
import { readDraft, writeDraft, clearDraft } from '../lib/onboardingDraft';
import { ModalOpenMarker } from '../hooks/useModalOpen';
import { deriveRoleFlags } from '../hooks/useUserRole';
import { canChangeOffice, officeWritePatch } from '../lib/profileOffice'; // RW-2989

// Human-readable step labels for analytics. Keep in sync with the step
// blocks rendered below — order matters because step indices are 1-based.
const STEP_NAMES = ['welcome', 'profile_basics', 'about_you', 'photo', 'complete'];

// Image processing moved to FileUpload component

/* ─── Constants ─── */
// OFFICES / SPECIALTIES / SERVICE_AREA_GROUPS / toggleItem / TerritorySection
// now live in the shared profile-field module (imported above) so the wizard
// and ProfileEditor never drift (RW-773).
const TOTAL_STEPS = 5;

/* ─── Main Component ─── */
export default function OnboardingFlow({ user, onComplete }) {
  const { triggerCelebration } = useCelebration();
  // RW-2266: read once, before the first render, so the wizard opens where the
  // person left off instead of back at "Welcome".
  const [draft] = useState(() => readDraft(user?.uid) || {});
  const [step, setStep] = useState(draft.step || 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 2 — Profile basics
  const [displayName, setDisplayName] = useState(draft.displayName ?? (user?.displayName || ''));
  const [phone, setPhone] = useState(draft.phone ?? '');
  // RW-2989: an office already on the profile (the Agent Roster sets it for
  // linked agents) is the person's — only an admin changes it. The wizard
  // shows it read-only and never sends it; a person with no office yet picks
  // their first one here (firestore.rules users `selfOfficeWriteOk`).
  const isAdminUser = deriveRoleFlags(user?.role).isAdmin;
  const officeDoc = { officeId: typeof user?.officeId === 'string' ? user.officeId : '' };
  const officeLocked = !canChangeOffice({ isAdmin: isAdminUser, doc: officeDoc });
  const [office, setOffice] = useState(officeDoc.officeId || draft.office || '');
  // The profile doc can land after the first render — a locked office always
  // shows (and validates as) the one on file.
  useEffect(() => {
    if (officeLocked && office !== officeDoc.officeId) setOffice(officeDoc.officeId);
  }, [officeLocked, office, officeDoc.officeId]);
  const [titleLabel, setTitleLabel] = useState(draft.titleLabel ?? '');

  // Step 3 — About you
  const [bio, setBio] = useState(draft.bio ?? '');
  const [specialties, setSpecialties] = useState(draft.specialties ?? []);
  const [serviceAreas, setServiceAreas] = useState(draft.serviceAreas ?? { newOrleans: [], northshore: [], batonRouge: [] });

  // Step 4 — Photo
  const [previewUrl, setPreviewUrl] = useState(draft.previewUrl ?? (user?.photoURL || null));
  // pendingBlob and uploadProgress removed — FileUpload handles uploads
  // photoError and fileInputRef no longer needed — FileUpload handles them

  // RW-2266: keep the resume point current. Cleared by handleFinish once the
  // profile write succeeds, and by writeDraft itself outside steps 2-4.
  useEffect(() => {
    if (!user?.uid) return;
    writeDraft(user.uid, { step, displayName, phone, office, titleLabel, bio, specialties, serviceAreas, previewUrl });
  }, [user?.uid, step, displayName, phone, office, titleLabel, bio, specialties, serviceAreas, previewUrl]);

  // Prevent body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // RW-23: Fire ONBOARDING_STARTED once when the modal first mounts. The
  // user is brand-new at this point so initAnalytics() may not have been
  // called yet — use trackWithUser() to write directly with the auth user.
  useEffect(() => {
    trackWithUser(EVENTS.ONBOARDING_STARTED, user, { totalSteps: TOTAL_STEPS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstName = String(user?.displayName || 'there').split(' ')[0];

  // Validation per step
  const canProceed = () => {
    // RW-3530: the phone rule is shared with the directory's completeness chip.
    if (step === 2) return hasProfilePhone(phone) && office.length > 0;
    if (step === 4) return true; // Photo is optional (skip allowed)
    return true;
  };

  // Next step
  const handleNext = () => {
    setError('');
    if (step === 2) {
      if (!hasProfilePhone(phone)) { setError('Phone number is required'); return; }
      if (!office) { setError('Please select your office'); return; }
    }
    // Photo is optional — track skip
    if (step < TOTAL_STEPS) {
      // RW-23: Fire ONBOARDING_STEP_COMPLETED for the step we're leaving.
      trackWithUser(EVENTS.ONBOARDING_STEP_COMPLETED, user, {
        step,
        stepName: STEP_NAMES[step - 1] || `step_${step}`,
      });
      setStep(step + 1);
    }
  };

  // Final submit — FileUpload component handles the actual upload,
  // previewUrl already contains the Firebase Storage download URL
  const handleFinish = async () => {
    setSaving(true);
    setError('');
    // Step 4 doesn't go through handleNext (the Finish button calls
    // handleFinish directly), so fire its STEP_COMPLETED event here.
    trackWithUser(EVENTS.ONBOARDING_STEP_COMPLETED, user, {
      step: 4,
      stepName: STEP_NAMES[3] || 'photo',
      photoSkipped: !previewUrl,
    });
    try {
      const photoURL = previewUrl || '';

      await setDocument('users', user.uid, withTenant('users', {
        // RW-729: this flow completes the PROFILE/welcome step — NOT the
        // onboarding CHECKLIST. `onboardingComplete` is the checklist-done flag
        // (sourced from instance status by the toggle callable + App.js:680), so
        // setting it here was the drift source (profile-done ≠ checklist-done).
        // The welcome-modal gate (App.js:658) hides on `profileComplete` too, so
        // dropping it here doesn't re-surface the modal.
        profileComplete: true,
        profileCompletedAt: serverTimestamp(),
        joinedAt: serverTimestamp(),
        displayName: displayName.trim() || user.displayName || '',
        email: user.email || '',
        photoURL: photoURL || '',
        phone: phone.trim(),
        // RW-1383 triple (officeId / office / primaryOffice), RW-2989: only when
        // this person may set it — a locked office is never sent.
        ...officeWritePatch({ isAdmin: isAdminUser, doc: officeDoc, chosen: office }),
        title: titleLabel.trim(),
        bio: bio.trim(),
        specialties,
        serviceAreas,
        // RW-1202: the wizard must NOT write `role`. It completes the PROFILE;
        // role is owned by provisionRoleClaim (canonical lowercase `role` +
        // `roles[]`). The old `role: 'Agent'` here both broke the
        // canonical-lowercase convention (PeopleDirectory/OrgChart) AND clobbered
        // any staff role (trainer/tc/…) to Agent on wizard completion.
        isActive: true,
        lastSeen: serverTimestamp(),
        photoSkipped: !previewUrl,
      }), { merge: true });

      // RW-1203: stamp the same-browser "done" flag ONLY after the profile
      // write succeeds. Stamping it BEFORE the write (the prior behavior) meant
      // a failed or aborted save still suppressed the welcome modal on the next
      // login from this browser, stranding the user with an incomplete profile
      // (no phone/office) and no re-prompt. The Firestore write is the
      // cross-device source of truth; this flag is only a same-browser
      // fast-path and must never outrun a confirmed write.
      try { localStorage.setItem(`reve_onboarding_done_${user.uid}`, '1'); } catch {}
      // RW-2266: the answers are in Firestore now; the browser copy is done.
      clearDraft(user.uid);

      // RW-23: Final completion event. Use trackWithUser because the user
      // doc was just written this tick — initAnalytics() may not yet see
      // the role/office values.
      trackWithUser(EVENTS.ONBOARDING_COMPLETED, user, {
        office,
        photoSkipped: !previewUrl,
        specialtiesCount: specialties.length,
      });

      // RW-1199 (B2): finalize onboarding SERVER-SIDE — the single source of
      // truth. Replaces the old client fire-and-forget auto-assign block (which
      // could silently fail and produce a counts-less instance). Awaited so a
      // failure isn't swallowed; the callable is idempotent and, now that office
      // is known, applies office-specific items (B3). Non-blocking by design:
      // the onUserCreated trigger already created the checklist at sign-in and a
      // daily reconcile cron backstops, so a finalize hiccup never blocks the
      // wizard — the agent still lands in the app.
      try {
        const finalize = httpsCallable(getFunctions(), 'finalizeOnboarding');
        await finalize({});
      } catch (e) {
        console.warn('finalizeOnboarding failed (non-blocking — trigger + reconcile backstop):', e);
      }

      // Celebration!
      triggerCelebration('confetti', `Welcome to Rêve, ${firstName}!`);
      setStep(TOTAL_STEPS);
      setTimeout(() => onComplete(), 100);
    } catch (err) {
      console.error('Onboarding save failed:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  // Progress bar
  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;

  return (
    <div style={S.overlay} role="dialog" aria-modal="true" aria-label="Welcome to Rêve Workspace">
      <ModalOpenMarker />
      <div style={{ ...S.card, ...(isMobile ? S.cardMobile : {}) }}
        onDragOver={(e) => e.preventDefault()}>

        {/* Progress bar */}
        <div style={S.progressTrack} role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label={`Onboarding progress: ${progress}%`}>
          <div style={{ ...S.progressFill, width: `${progress}%` }} />
        </div>
        <p style={S.stepLabel}>Step {step} of {TOTAL_STEPS}</p>

        {/* ═══ STEP 1 — Welcome ═══ */}
        {step === 1 ? (
          <div style={S.centered}>
            <div style={S.logo}>rêve</div>
            <h1 style={S.heading}>Welcome to Rêve Workspace, {firstName}.</h1>
            <p style={S.subtext}>Let's set up your profile so your team knows who you are.</p>
            {/* RW-2244: the URL carries no step. RW-2266: the escapes here rendered
                literally ("aren\u2019t"), and the sentence is no longer true — the
                answers are kept in this browser until you finish. */}
            <p style={{ ...S.subtext, fontSize: 12, opacity: 0.8 }}>Takes about a minute — we'll remember where you left off if you close this.</p>
            <button type="button" style={S.primaryBtn} onClick={() => setStep(2)}>Let's get started</button>
          </div>
        ) : null}

        {/* ═══ STEP 2 — Profile basics ═══ */}
        {step === 2 ? (
          <div>
            <h2 style={S.heading}>Profile basics</h2>
            {error ? <p role="alert" style={S.error}>{error}</p> : null}

            <div style={S.field}>
              <label htmlFor="obflow-display-name" style={S.label}>Display Name</label>
              <input id="obflow-display-name" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                style={S.input} placeholder="Your full name" />
            </div>

            <div style={S.field}>
              <label htmlFor="obflow-phone" style={S.label}>Phone <span style={S.required}>*</span></label>
              <input id="obflow-phone" type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setError(''); }}
                style={{ ...S.input, borderColor: error && !phone.trim() ? colors.errorRed : colors.pearl }}
                placeholder="(504) 555-0100" />
            </div>

            <div style={S.field}>
              <label id="obflow-office-label" style={S.label}>Office <span style={S.required}>*</span></label>
              <div role="group" aria-labelledby="obflow-office-label" style={S.chipRow}>
                {OFFICES.map((o) => (
                  <button key={o.id} type="button" disabled={officeLocked} aria-pressed={office === o.id}
                    onClick={() => { setOffice(o.id); setError(''); }}
                    style={{ ...S.chip, ...(office === o.id ? S.chipActive : {}), ...(officeLocked && office !== o.id ? { opacity: 0.5, cursor: 'default' } : {}) }}>{o.label}</button>
                ))}
              </div>
              {officeLocked ? (
                <div style={{ fontSize: 12, color: colors.gray1, marginTop: 6 }}>Ask an admin to change your office.</div>
              ) : null}
            </div>

            <div style={S.field}>
              <label htmlFor="obflow-title" style={S.label}>Title / Role <span style={{ color: colors.gray1, fontWeight: 400 }}>(optional)</span></label>
              <input id="obflow-title" type="text" value={titleLabel} onChange={(e) => setTitleLabel(e.target.value)}
                style={S.input} placeholder="e.g. REALTOR®, Broker Associate" maxLength={80} />
            </div>

            <button type="button" disabled={!canProceed()} style={{ ...S.primaryBtn, opacity: canProceed() ? 1 : 0.5 }} onClick={handleNext}>Continue</button>
          </div>
        ) : null}

        {/* ═══ STEP 3 — About you ═══ */}
        {step === 3 ? (
          <div>
            <h2 style={S.heading}>About you</h2>

            <div style={S.field}>
              <label htmlFor="obflow-bio" style={S.label}>Bio <span style={{ color: colors.gray1, fontWeight: 400 }}>(optional)</span></label>
              <textarea id="obflow-bio" value={bio} onChange={(e) => { if (e.target.value.length <= 280) setBio(e.target.value); }}
                style={S.textarea} placeholder="A short bio about yourself…" rows={3} />
              <p style={{ margin: '4px 0 0', fontSize: 11, color: bio.length > 260 ? colors.errorRed : colors.gray1, textAlign: 'right' }}>{bio.length}/280</p>
            </div>

            <div style={S.field}>
              <label id="obflow-specialties-label" style={S.label}>Specialties</label>
              <div role="group" aria-labelledby="obflow-specialties-label" style={S.chipRow}>
                {SPECIALTIES.map((s) => (
                  <button key={s} type="button" onClick={() => setSpecialties(toggleItem(specialties, s))}
                    style={{ ...S.chip, ...(specialties.includes(s) ? S.chipActive : {}) }}>{s}</button>
                ))}
              </div>
            </div>

            <div style={S.field}>
              <label id="obflow-areas-label" style={S.label}>Service Areas</label>
              <div role="group" aria-labelledby="obflow-areas-label">
                {SERVICE_AREA_GROUPS.map((group) => (
                  <TerritorySection
                    key={group.key}
                    group={group}
                    selected={serviceAreas[group.key] || []}
                    onChange={(newAreas) => setServiceAreas((prev) => ({ ...prev, [group.key]: newAreas }))}
                  />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" style={S.ghostBtn} onClick={() => setStep(2)}>Back</button>
              <button type="button" style={S.primaryBtn} onClick={handleNext}>Continue</button>
            </div>
          </div>
        ) : null}

        {/* ═══ STEP 4 — Profile photo ═══ */}
        {step === 4 ? (
          <div>
            <h2 style={S.heading}>Add your photo</h2>
            <p style={S.subtext}>Your teammates want to see your face.</p>
            {error ? <p role="alert" style={S.error}>{error}</p> : null}

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FileUpload
                storagePrefix={`avatars/${user.uid}`}
                maxSizeMB={1}
                outputFormat="webp"
                aspectRatio={1}
                existingUrl={previewUrl}
                label="Upload your photo"
                onUploadComplete={(url) => {
                  setPreviewUrl(url);
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" style={S.ghostBtn} onClick={() => setStep(3)}>Back</button>
              <button type="button" disabled={saving} onClick={handleFinish}
                style={{ ...S.primaryBtn, opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Saving your profile…' : 'Finish setup'}
              </button>
            </div>
            {!previewUrl ? (
              <button type="button" onClick={handleFinish} disabled={saving}
                style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', marginTop: 12, fontFamily: "'Jost', sans-serif", display: 'block', textAlign: 'center', width: '100%' }}>
                Skip for now
              </button>
            ) : null}
          </div>
        ) : null}

        {/* ═══ STEP 5 — Done ═══ */}
        {step === 5 ? (
          <div style={S.centered}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h2 style={S.heading}>You're all set.</h2>
            <p style={S.subtext}>Welcome to the team, {firstName}.</p>
            <button type="button" style={S.primaryBtn} onClick={onComplete}>Go to my feed</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ─── Styles ─── */
const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(15,16,17,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    fontFamily: fonts.body, backdropFilter: 'blur(4px)',
  },
  card: {
    background: colors.white, borderRadius: radius.lg, width: '100%', maxWidth: 480,
    padding: `${spacing.xl} ${spacing.lg}`, boxShadow: '0 16px 64px rgba(15,16,17,0.2)',
    maxHeight: '92vh', overflowY: 'auto', position: 'relative',
  },
  cardMobile: {
    maxWidth: '100%', borderRadius: `${radius.lg} ${radius.lg} 0 0`,
    position: 'fixed', bottom: 0, left: 0, right: 0, top: 'auto',
    maxHeight: '90vh',
  },
  progressTrack: {
    height: 4, background: colors.pearl, borderRadius: 2, overflow: 'hidden', marginBottom: 8,
  },
  progressFill: {
    height: '100%', background: colors.coral, borderRadius: 2, transition: 'width 0.3s ease',
  },
  stepLabel: {
    fontSize: 11, fontWeight: 600, color: colors.gray1, letterSpacing: '0.08em',
    textTransform: 'uppercase', marginBottom: 20,
  },
  centered: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '16px 0',
  },
  logo: {
    fontSize: 36, fontWeight: 700, color: colors.noir, fontFamily: fonts.heading,
    letterSpacing: '0.04em', marginBottom: 16,
  },
  heading: {
    fontSize: 22, fontWeight: 700, color: colors.noir, fontFamily: fonts.heading, margin: '0 0 8px',
  },
  subtext: {
    fontSize: 15, color: colors.gray2, lineHeight: 1.5, margin: '0 0 24px',
  },
  error: {
    fontSize: 13, color: colors.errorRed, margin: '0 0 12px', fontWeight: 500,
  },
  field: { marginBottom: 18 },
  label: {
    display: 'block', fontSize: 13, fontWeight: 600, color: colors.noir, marginBottom: 6,
  },
  required: { color: colors.coralInk },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: radius.sm,
    border: `1.5px solid ${colors.pearl}`, fontSize: 15, fontFamily: fonts.body,
    color: colors.noir, outline: 'none', background: colors.white,
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: radius.sm,
    border: `1.5px solid ${colors.pearl}`, fontSize: 15, fontFamily: fonts.body,
    color: colors.noir, outline: 'none', resize: 'vertical', minHeight: 80,
  },
  chipRow: {
    display: 'flex', flexWrap: 'wrap', gap: 8,
  },
  chip: {
    padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${colors.pearl}`,
    background: colors.white, color: colors.gray3, fontSize: 13, fontWeight: 500,
    fontFamily: fonts.body, cursor: 'pointer', transition: 'all 0.15s',
  },
  chipActive: {
    // Whole shorthand, not just the colour — see RW-1969 and the note on
    // styles.chipActive: React skips the unchanged `border` and strips only the
    // longhand on deselect, losing the pearl colour.
    border: `1.5px solid ${colors.coral}`,
    background: colors.coral, color: colors.white, fontWeight: 600,
  },
  primaryBtn: {
    width: '100%', padding: '13px 24px', borderRadius: radius.sm, border: 'none',
    background: colors.coral, color: colors.white, fontSize: 16, fontWeight: 600,
    fontFamily: fonts.body, cursor: 'pointer', marginTop: 8,
  },
  ghostBtn: {
    flex: 1, padding: '13px 24px', borderRadius: radius.sm, border: `1.5px solid ${colors.pearl}`,
    background: 'transparent', color: colors.gray3, fontSize: 15, fontWeight: 500,
    fontFamily: fonts.body, cursor: 'pointer', marginTop: 8,
  },
  photoArea: {
    width: 180, height: 180, borderRadius: '50%', margin: '16px auto',
    border: `3px dashed ${colors.pearl}`, cursor: 'pointer', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'border-color 0.2s',
  },
  photoPreview: {
    width: '100%', height: '100%', objectFit: 'cover',
  },
  photoPlaceholder: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
};
